import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Clock, Search } from 'lucide-react';
import Toast, { ToastMessage } from '../common/Toast';
import Badge from '../common/Badge';
import FormControl from '../common/FormControl';
import Skeleton from '../common/Skeleton';
import NoDataFound from '../common/NoDataFound';
import FileAnalyticsAPI from '../file_analytics/fileAnalyticsService';
import DataPreviewModal from '../file_analytics/modals/DataPreviewModal';
import { RuleBoardRow } from '../file_analytics/types';
import { formatDate } from '../file_analytics/mockData';
import { severityVariant } from '../../utils/statusVariant';
import { usePermission } from '../../contexts/PermissionContext';

const ACT_RESULTS = 'CMG_EVENTS';

/**
 * What every detection rule last found — a history list, read once.
 *
 * Runs are started and watched on the Continuous Monitoring screen
 * (components/file_analytics/FileAnalyticsTab.tsx), which is where the live
 * progress of a run is shown. This screen deliberately holds no live connection:
 * it answers "what did each rule find when it last ran", which is a question
 * about the past. Keeping the running animation in one place only stops the two
 * screens drifting apart.
 *
 * A rule that has never run shows "not yet run" rather than zero — an absence of
 * results is not a result of zero.
 */

interface SourceGroup {
  sourceId: number;
  sourceName: string;
  rules: RuleBoardRow[];
  totalFound: number;
  rowsExamined: number;
  lastRun?: string | null;
}

const CmResultsView: React.FC = () => {
  const [rules, setRules] = useState<RuleBoardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [preview, setPreview] = useState<{ runId: number; title: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const { allowed: canView, loading: viewLoading } = usePermission(ACT_RESULTS, 'view');

  /**
   * One reading for the whole screen. This was assembled with one request per
   * pipeline plus a summary request each; against a remote database at roughly a
   * seventh of a second per round trip, a screen that opens with sixteen separate
   * readings feels broken even when it is working.
   */
  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const board = await FileAnalyticsAPI.ruleBoard();
      setRules(board.rules);
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.detail || 'Could not load rule results' });
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => { load(); }, [load]);

  const groups: SourceGroup[] = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const bySource = new Map<number, SourceGroup>();

    for (const r of rules) {
      if (term &&
          !r.rule_name.toLowerCase().includes(term) &&
          !r.source_name.toLowerCase().includes(term)) continue;

      let g = bySource.get(r.source_id);
      if (!g) {
        g = { sourceId: r.source_id, sourceName: r.source_name,
              rules: [], totalFound: 0, rowsExamined: 0, lastRun: null };
        bySource.set(r.source_id, g);
      }
      g.rules.push(r);
      g.totalFound += r.rows_matched ?? 0;
      // Every rule on a source reads the same cleaned layer, so this is the size
      // of that layer, not a sum across rules.
      g.rowsExamined = Math.max(g.rowsExamined, r.rows_in ?? 0);
      if (r.finished_at && (!g.lastRun || r.finished_at > g.lastRun)) g.lastRun = r.finished_at;
    }
    return Array.from(bySource.values());
  }, [rules, searchTerm]);

  if (!viewLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to monitoring results." />
      </div>
    );
  }

  return (
    <>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div className="netbody-card side-content-card flex flex-col h-full" data-testid="cm-results-root">

        <div className="netbody-header">
          <div className="flex items-center gap-2 ml-auto">
            <FormControl.Input
              type="text"
              placeholder="Search rules…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              leadingIcon={<Search size={14} />}
              className="filter-search"
            />
          </div>
        </div>

        <div className="netbody-content">
          <div className="scroll-container flex-1 overflow-hidden">

            {loading && (
              <div className="table-list">
                <div className="table-list-scroll">
                  <table className="table-list-table">
                    <tbody aria-busy>
                      <Skeleton.TableRows
                        cols={['2xs', 'md', 'pill', { width: 'sm', align: 'right' },
                               { width: 'sm', align: 'right' }, { width: 'xs', align: 'right' }]} />
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!loading && groups.length === 0 && (
              <NoDataFound
                title="No detection rules yet"
                message="Rules written against a data source will appear here with what each one found."
              />
            )}

            {!loading && groups.map(g => (
              <div className="table-list" key={g.sourceId} style={{ marginBottom: 18 }}>
                <div className="flex items-baseline gap-2 flex-wrap" style={{ padding: '10px 12px 8px' }}>
                  <strong style={{ fontSize: 14 }}>{g.sourceName}</strong>
                  <span className="text-[var(--text-muted)]" style={{ fontSize: 12 }}>
                    {g.rules.length} {g.rules.length === 1 ? 'rule' : 'rules'}
                    {g.rowsExamined > 0 && <> · {g.rowsExamined.toLocaleString()} rows examined</>}
                  </span>
                  {g.lastRun && (
                    <span className="inline-flex items-center gap-1 text-[var(--text-muted)] ml-auto"
                          style={{ fontSize: 12 }}>
                      <Clock size={12} /> {formatDate(g.lastRun)}
                    </span>
                  )}
                </div>

                <div className="table-list-scroll">
                  <table className="table-list-table">
                    <thead>
                      <tr>
                        <th className="table-list-th table-list-cell--sno">S.No</th>
                        <th className="table-list-th">Rule</th>
                        <th className="table-list-th">Severity</th>
                        <th className="table-list-th table-list-th--right">Examined</th>
                        <th className="table-list-th table-list-th--right">Found</th>
                        <th className="table-list-th table-list-th--right">Newly found</th>
                      </tr>
                    </thead>
                    <tbody aria-live="polite">
                      {g.rules.map((r, idx) => {
                        const notYetRun = r.run_id == null;
                        // A detection run records the file it wrote but no dataset
                        // row, so its findings are opened through the run itself.
                        const canPreview = r.run_id != null && !!r.output_s3_key;
                        return (
                          <tr
                            key={r.rule_id}
                            className="table-list-row"
                            data-testid={`cm-rule-row-${r.rule_id}`}
                            onClick={() => canPreview && setPreview({
                              runId: r.run_id as number,
                              title: `Matched — ${r.rule_name}`,
                            })}
                            style={{
                              cursor: canPreview ? 'pointer' : 'default',
                              opacity: notYetRun ? 0.55 : 1,
                            }}
                          >
                            <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                            <td className="table-list-cell">
                              <span className="table-list-cell-title">{r.rule_name}</span>
                            </td>
                            <td className="table-list-cell">
                              {r.run_status === 'Failed'
                                ? <Badge variant="danger" size="sm">Failed</Badge>
                                : <Badge variant={severityVariant(r.severity)} size="sm">
                                    {r.severity || 'Not set'}
                                  </Badge>}
                            </td>
                            <td className="table-list-cell table-list-cell--right">
                              {r.rows_in != null ? r.rows_in.toLocaleString() : '—'}
                            </td>
                            <td className="table-list-cell table-list-cell--right">
                              {notYetRun
                                ? <span className="text-[var(--text-muted)]">not yet run</span>
                                : <strong>{(r.rows_matched ?? 0).toLocaleString()}</strong>}
                            </td>
                            <td className="table-list-cell table-list-cell--right">
                              {r.rows_new == null
                                ? <span className="text-[var(--text-muted)]"
                                        title="No earlier run to compare against">—</span>
                                : r.rows_new > 0
                                  ? <Badge variant="warning" size="sm">+{r.rows_new.toLocaleString()}</Badge>
                                  : <span className="text-[var(--text-muted)]">0</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <DataPreviewModal
        isOpen={!!preview}
        onClose={() => setPreview(null)}
        runId={preview?.runId ?? null}
        title={preview?.title}
      />
    </>
  );
};

export default CmResultsView;
